import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline as UnderlineIcon, Eraser, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

const SIZES = [
  { label: "Small", px: "13px" },
  { label: "Normal", px: "16px" },
  { label: "Large", px: "20px" },
  { label: "X-Large", px: "26px" },
  { label: "Huge", px: "34px" },
];

export function RichTextEditor({ value, onChange, placeholder = "Start typing…", testid }) {
  const ref = useRef(null);
  const last = useRef("");
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    if (ref.current && (value || "") !== last.current) {
      ref.current.innerHTML = value || "";
      last.current = value || "";
      setEmpty(!ref.current.textContent.trim());
    }
  }, [value]);

  const emit = () => {
    const html = ref.current.innerHTML;
    last.current = html;
    setEmpty(!ref.current.textContent.trim());
    onChange(html);
  };

  const selectContents = (node) => {
    const sel = window.getSelection();
    const r = document.createRange();
    r.selectNodeContents(node);
    sel.removeAllRanges();
    sel.addRange(r);
  };

  const wrapInline = (tag) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const el = document.createElement(tag);
    try {
      range.surroundContents(el);
    } catch (e) {
      const frag = range.extractContents();
      el.appendChild(frag);
      range.insertNode(el);
    }
    selectContents(el);
  };

  const cmd = (command, fallbackTag) => {
    ref.current.focus();
    const before = ref.current.innerHTML;
    try { document.execCommand("styleWithCSS", false, false); } catch (e) {}
    document.execCommand(command, false, null);
    if (fallbackTag && ref.current.innerHTML === before) wrapInline(fallbackTag);
    emit();
  };

  const applySize = (px) => {
    if (!px) return;
    ref.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontSize = px;
    try {
      range.surroundContents(span);
    } catch (e) {
      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    selectContents(span);
    emit();
  };

  const clearFmt = () => {
    ref.current.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch (e) {}
    document.execCommand("removeFormat", false, null);
    emit();
  };

  const align = (cmd) => {
    ref.current.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch (e) {}
    document.execCommand(cmd, false, null);
    emit();
  };

  // Block OS/browser autocorrect from turning "(c)" into "©" (and (r)/(tm) too).
  const onBeforeInput = (e) => {
    const it = e.nativeEvent && e.nativeEvent.inputType;
    const d = e.nativeEvent && e.nativeEvent.data;
    if (it === "insertReplacementText" && d && /[©®™]/.test(d)) {
      e.preventDefault();
    }
  };

  const btn = "h-8 w-8 rounded-md grid place-items-center transition-colors";
  const btnStyle = { color: "#B7BDD6", background: "transparent" };

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: "1px solid #1E2233", background: "#0E1119" }} data-testid={testid ? `${testid}-wrap` : undefined}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 flex-wrap" style={{ borderBottom: "1px solid #1E2233", background: "#12151F" }}>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("bold", "b")} className={btn} style={btnStyle} title="Bold" data-testid={testid ? `${testid}-bold` : undefined}><Bold size={15} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("italic", "i")} className={btn} style={btnStyle} title="Italic" data-testid={testid ? `${testid}-italic` : undefined}><Italic size={15} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("underline", "u")} className={btn} style={btnStyle} title="Underline" data-testid={testid ? `${testid}-underline` : undefined}><UnderlineIcon size={15} /></button>
        <div className="w-px h-5 mx-1" style={{ background: "#1E2233" }} />
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { applySize(e.target.value); e.target.selectedIndex = 0; }}
          defaultValue=""
          className="h-8 rounded-md px-2 text-xs outline-none"
          style={{ background: "#0E1119", border: "1px solid #1E2233", color: "#B7BDD6" }}
          title="Text size (select text first)"
          data-testid={testid ? `${testid}-size` : undefined}
        >
          <option value="" disabled>Text size</option>
          {SIZES.map((s) => <option key={s.px} value={s.px}>{s.label}</option>)}
        </select>
        <div className="w-px h-5 mx-1" style={{ background: "#1E2233" }} />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => align("justifyLeft")} className={btn} style={btnStyle} title="Align left" data-testid={testid ? `${testid}-align-left` : undefined}><AlignLeft size={15} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => align("justifyCenter")} className={btn} style={btnStyle} title="Center" data-testid={testid ? `${testid}-align-center` : undefined}><AlignCenter size={15} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => align("justifyRight")} className={btn} style={btnStyle} title="Align right" data-testid={testid ? `${testid}-align-right` : undefined}><AlignRight size={15} /></button>
        <div className="w-px h-5 mx-1" style={{ background: "#1E2233" }} />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={clearFmt} className={btn} style={btnStyle} title="Clear formatting" data-testid={testid ? `${testid}-clear` : undefined}><Eraser size={15} /></button>
      </div>

      {/* Editable area */}
      <div className="relative">
        {empty && (
          <div className="absolute top-3 left-4 text-sm pointer-events-none" style={{ color: "#5A6081" }}>{placeholder}</div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBeforeInput={onBeforeInput}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-gramm="false"
          data-testid={testid}
          className="rte-editor px-4 py-3 text-sm outline-none overflow-y-auto"
          style={{ minHeight: 340, maxHeight: 560, color: "#E7E4DB", lineHeight: 1.7 }}
        />
      </div>
    </div>
  );
}
